import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon as GitBookIcon, type IconName as GitBookIconName } from '@gitbook/icons';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    type IconDefinition,
    renderIcon,
} from '@gitbook/spine-react';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { NavigationLink } from '../../navigation';
import { useContentReferenceContext } from '../ContentReferences';
import { SpaceLinkSuspense } from '../Space';

/**
 * Display logs from a message or step.
 */
export function ToolCallsSummary(props: { toolCalls: api.AIToolCall[]; loading?: boolean }) {
    const { toolCalls, loading = false } = props;

    const groups = React.useMemo(() => {
        return groupToolCalls(toolCalls);
    }, [toolCalls]);

    return (
        <div className="flex flex-col py-2 ">
            {groups.map((group, index) => (
                <ToolCallsGroup key={index} toolCalls={group} loading={loading} />
            ))}
        </div>
    );
}

/**
 * Display logs from a message or step.
 */
function ToolCallsGroup(props: { toolCalls: api.AIToolCall[]; loading?: boolean }) {
    const { toolCalls, loading = false } = props;
    const firstToolCall = toolCalls[0];
    assertIsDefined(firstToolCall);

    const groupLabel = getToolCallGroupLabel(firstToolCall.tool, toolCalls);
    if (toolCalls.length === 1 || !groupLabel) {
        return (
            <div className="flex flex-col pl-2">
                {toolCalls.map((toolCall, index) => (
                    <ToolCallSummary key={index} toolCall={toolCall} />
                ))}
            </div>
        );
    }

    return (
        <Disclosure>
            <div className="flex flex-col">
                <div className="flex flex-row">
                    <DisclosureTrigger>
                        <Button
                            kind="blank"
                            size="small"
                            label={groupLabel}
                            leadingIcon={getIconForToolCall(toolCalls[0])}
                            trailingIcon={<DisclosureChevron />}
                            className={loading ? 'animate-pulse' : ''}
                        />
                    </DisclosureTrigger>
                </div>
                <DisclosurePanel>
                    <div className="flex flex-col py-2 pl-7">
                        {toolCalls.map((toolCall, index) => (
                            <ToolCallSummary key={index} toolCall={toolCall} withIcon={false} />
                        ))}
                    </div>
                </DisclosurePanel>
            </div>
        </Disclosure>
    );
}

function ToolCallSummary(props: { toolCall: api.AIToolCall; withIcon?: boolean }) {
    const { toolCall, withIcon = true } = props;

    return (
        <p className="py-1 text-muted text-ui-small">
            {withIcon ? renderIcon(getIconForToolCall(toolCall), { className: 'mr-2' }) : null}
            {getDescriptionForToolCall(toolCall)}
        </p>
    );
}

function getDescriptionForToolCall(toolCall: api.AIToolCall): React.ReactNode {
    switch (toolCall.tool) {
        case 'getPageContent':
            return (
                <>
                    Read page <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'search':
            return (
                <>
                    Searched <strong>{toolCall.query}</strong>
                </>
            );
        case 'getPages':
            return (
                <>
                    Listed the pages
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'editPage':
            return (
                <>
                    Edited <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'createPage':
            return (
                <>
                    Created <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'deletePage':
            return (
                <>
                    Deleted <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'renamePage':
            return (
                <>
                    Renamed <PageLink page={toolCall.page} /> to <strong>{toolCall.title}</strong>
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'movePage':
            return (
                <>
                    Moved <PageLink page={toolCall.page} />
                    {toolCall.moveType === 'before' && toolCall.to ? (
                        <>
                            {' '}
                            before <PageLink page={toolCall.to} />
                        </>
                    ) : toolCall.moveType === 'after' && toolCall.to ? (
                        <>
                            {' '}
                            after <PageLink page={toolCall.to} />
                        </>
                    ) : toolCall.moveType === 'within' && toolCall.to ? (
                        <>
                            {' '}
                            into <PageLink page={toolCall.to} />
                        </>
                    ) : null}
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'listModifiedPages':
            return (
                <>
                    Listed the modified pages
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'getPageDiff':
            return (
                <>
                    Read the diff for page <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        case 'createChangeRequest':
            return (
                <>
                    {toolCall.action === 'create' ? 'Created a' : 'Updated'} change request{' '}
                    <NavigationLink
                        to={{
                            current: {
                                type: 'change_requests',
                                selected: {
                                    spaceId: toolCall.spaceId,
                                    changeRequestNumber: toolCall.changeRequest.number,
                                    sideSheet: {
                                        type: 'agent',
                                    },
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        "{toolCall.changeRequest.subject}" (#
                        {toolCall.changeRequest.number})
                    </NavigationLink>
                </>
            );
        case 'comment': {
            if (!toolCall.page) {
                return (
                    <>
                        Posted a comment
                        <OtherSpaceLink spaceId={toolCall.spaceId} />
                    </>
                );
            }
            return (
                <>
                    Posted{' '}
                    <NavigationLink
                        to={{
                            current: {
                                page: toolCall.page.path ?? '',
                                sideSheet: {
                                    type: 'comments',
                                    comment: toolCall.commentId,
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        a comment
                    </NavigationLink>{' '}
                    on <PageLink page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        }
        case 'replyToComment': {
            return (
                <>
                    Posted{' '}
                    <NavigationLink
                        to={{
                            current: {
                                page: toolCall.page?.path ?? '',
                                sideSheet: {
                                    type: 'comments',
                                    comment: toolCall.commentId,
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        a reply
                    </NavigationLink>
                    {toolCall.page ? (
                        <>
                            {' '}
                            on <PageLink page={toolCall.page} />
                        </>
                    ) : null}
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        }
        case 'submitChangeRequestReview': {
            const decision = toolCall.status === 'approved' ? 'Approved' : 'Changes requested';
            return (
                <>
                    Submitted{' '}
                    <NavigationLink
                        to={{
                            current: {
                                sideSheet: {
                                    type: 'comments',
                                    comment: toolCall.commentId,
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        a review
                    </NavigationLink>{' '}
                    ({decision})
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        }
        case 'mcp': {
            return (
                <>
                    Called <strong>{toolCall.mcpToolTitle ?? toolCall.mcpToolName}</strong>
                </>
            );
        }
        case 'other':
            return <>{toolCall.summary.text}</>;
        default:
            assertNever(toolCall);
    }
}

function getIconForToolCall(toolCall: api.AIToolCall): IconDefinition {
    switch (toolCall.tool) {
        case 'getPageContent':
            return Icon.Page;
        case 'search':
            return Icon.SearchOrAsk;
        case 'getPages':
            return Icon.AddPageGroup;
        case 'editPage':
            return Icon.Edit;
        case 'createPage':
            return Icon.Edit;
        case 'deletePage':
            return Icon.Edit;
        case 'renamePage':
            return Icon.Edit;
        case 'movePage':
            return Icon.PageDuplicate;
        case 'listModifiedPages':
            return Icon.Page;
        case 'getPageDiff':
            return Icon.Page;
        case 'createChangeRequest':
            return Icon.ChangeRequestDraft;
        case 'submitChangeRequestReview':
            return Icon.ChangeRequestOpen;
        case 'comment':
        case 'replyToComment':
            return Icon.Comment;
        case 'mcp':
            return Icon.MCP;
        case 'other':
            return <GitBookIcon icon={toolCall.summary.icon as GitBookIconName} />;
        default:
            assertNever(toolCall);
    }
}

function getToolCallGroupLabel<ToolType extends api.AIToolCall['tool']>(
    toolCallType: ToolType,
    toolCalls: Array<Extract<api.AIToolCall, { tool: ToolType }>>
): string | null {
    switch (toolCallType) {
        case 'listModifiedPages':
        case 'getPages':
        case 'mcp':
        case 'other':
        case 'submitChangeRequestReview':
        case 'createChangeRequest':
            return null;
        case 'getPageContent':
            return `Read ${toolCalls.length} pages`;
        case 'search':
            return `Searched ${toolCalls.length} queries`;
        case 'getPageDiff':
            return `Read the diff for ${toolCalls.length} pages`;
        case 'comment':
            return `Posted ${toolCalls.length} comments`;
        case 'replyToComment':
            return `Posted ${toolCalls.length} replies`;
        case 'editPage':
            return `Edited ${toolCalls.length} pages`;
        case 'createPage':
            return `Created ${toolCalls.length} pages`;
        case 'deletePage':
            return `Deleted ${toolCalls.length} pages`;
        case 'renamePage':
            return `Renamed ${toolCalls.length} pages`;
        case 'movePage':
            return `Moved ${toolCalls.length} pages`;
        default:
            assertNever(toolCallType);
    }
}

function groupToolCalls(toolCalls: api.AIToolCall[]): api.AIToolCall[][] {
    const byTool = new Map<api.AIToolCall['tool'], api.AIToolCall[]>();
    for (const toolCall of toolCalls) {
        const group = byTool.get(toolCall.tool);
        if (group) {
            group.push(toolCall);
        } else {
            byTool.set(toolCall.tool, [toolCall]);
        }
    }
    return Array.from(byTool.values());
}

function PageLink(props: { page: api.ChangedRevisionPage }) {
    const { page } = props;

    return (
        <NavigationLink
            to={{
                current: {
                    page: page.path ?? '',
                },
            }}
            className="text-inherit underline decoration-dashed"
        >
            {page.title}
        </NavigationLink>
    );
}

/**
 * Link to a space that is not the current space.
 */
function OtherSpaceLink(props: { spaceId: string; prefix?: React.ReactNode }) {
    const { spaceId, prefix = ' in ' } = props;
    const context = useContentReferenceContext();

    if (context.space === spaceId) {
        return null;
    }

    return (
        <>
            {prefix}
            <SpaceLinkSuspense spaceId={spaceId} />
        </>
    );
}
